import React from "react";
import { TrustSealGlyph, ChatBubbleGlyph } from "./cnGlyphs";

// =====================================================================
// CS1 — Badge and pills (Redesign 2.0, board `cs1` + token sheet §1.7).
// Pure presentational, exact artboard values, shared across the club-side
// N2/N3/C surfaces. All strings pass through as props/children so each
// surface keeps its own (i18n-scanned) copy.
//
// NOTE the RequestCard itself keeps the shipped V2 pill set (SPEC-EXTRACT
// §9); these v3 pills are for the surfaces AROUND the card (detail, rows,
// chips, marker, N3 boards).
// =====================================================================

// The locked trust-badge tooltip (brief Rule 2 — must not be reworded).
export const CLUB_VERIFIED_TOOLTIP =
  "This club has confirmed this need and this agent as its channel on the platform.";

// Club Verified badge — v3 style with the filled trust seal S1.
// size "standard" (cards/details, 11.5px, shield 11×13) or "hero"
// (CS1 display size, 13px, shield 13×15).
export const ClubVerifiedPill = ({ size = "standard", className = "" }) => {
  const hero = size === "hero";
  return (
    <span
      title={CLUB_VERIFIED_TOOLTIP}
      className={`inline-flex items-center rounded-full bg-[#ECFDF5] text-[#047857] font-semibold ${
        hero
          ? "gap-1.5 text-[13px] pt-[5px] pb-[5px] pl-2.5 pr-[13px]"
          : "gap-[5px] text-[11.5px] pt-[3px] pb-[3px] pl-2 pr-2.5"
      } ${className}`}
    >
      <TrustSealGlyph width={hero ? 13 : 11} height={hero ? 15 : 13} />
      Club Verified
    </span>
  );
};

// Window pill ("Summer 2026") — §1.7.
export const WindowPill = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full bg-[#F3F4F6] text-[#6B7280] text-[11.5px] font-semibold px-2.5 py-[3px] ${className}`}
  >
    {children}
  </span>
);

// Status chips (Trusted Agent axis) — sent / accepted / declined / new.
// In-context size 10.5px / 2px 8px; CS1 display size 11.5px / 3px 10px
// (SPEC-EXTRACT §7.5: in-context values win per surface).
const STATUS_CHIP = {
  sent: "bg-[#F3F4F6] text-[#6B7280]",
  accepted: "bg-[#ECFDF5] text-[#16A34A]",
  declined: "bg-[#FEE2E2] text-[#DC2626]",
  new: "bg-[#E8F1FF] text-[#2283FF]",
};

export const StatusChip = ({ kind = "sent", display = false, children, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full font-semibold ${
      STATUS_CHIP[kind] || STATUS_CHIP.sent
    } ${display ? "text-[11.5px] px-2.5 py-[3px]" : "text-[10.5px] px-2 py-[2px]"} ${className}`}
  >
    {children}
  </span>
);

// The "Licensed" pill was REPLACED by the FIFA Verified badge (design D-A):
// components/common/FifaVerifiedBadge.js. Badge rule (Marin 2026-08-04):
// self-declaration carries the badge; only an explicit failed check
// ('failed_qr') hides it — see isFifaVerified. The CS3 rule carries over:
// no badge means NOTHING renders — never an "Unlicensed" label.

// Trust pill ("14 verified requests") — detail agent panel (white/bordered)
// or CS1 chip-family variant (gray fill).
export const TrustCountPill = ({ variant = "panel", children, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full font-semibold ${
      variant === "chip"
        ? "bg-[#F3F4F6] text-[#4B5563] text-[11.5px] px-2.5 py-[3px]"
        : "bg-white text-[#4B5563] border border-[#E5E7EB] text-[11px] px-[9px] py-[3px]"
    } ${className}`}
  >
    {children}
  </span>
);

// Contacted marker (CS1) — S2 bubble + label + "Open chat →" affordance.
// The chat is the relationship — no offer statuses, no other tracking.
// `children` defaults to the artboard string; the feed card passes the
// existing attribution copy ("Contacted by {name} · {date}") when known.
export const ContactedMarker = ({
  children = "You contacted this agent",
  onOpenChat,
  chatLabel = "Open chat →",
  className = "",
}) => (
  <div
    className={`flex items-center gap-[7px] bg-[#E8F1FF] rounded-lg pl-[11px] pr-[11px] py-2 ${className}`}
  >
    <ChatBubbleGlyph size={12} className="flex-none" />
    <span className="text-[11.5px] font-semibold text-[#1B6FE0] min-w-0 truncate">
      {children}
    </span>
    {onOpenChat && (
      <button
        type="button"
        onClick={onOpenChat}
        className="ml-auto flex-none text-[11.5px] font-bold text-[#2283FF] whitespace-nowrap"
      >
        {chatLabel}
      </button>
    )}
  </div>
);

// Removable filter chip (N2-1f) — label + white × disc. The remove glyph is
// the verbatim U+00D7 MULTIPLICATION SIGN (brief Rule 2), never an icon.
export const RemovableFilterChip = ({ children, onRemove, removeLabel = "Remove filter", className = "" }) => (
  <span
    className={`inline-flex items-center gap-1.5 bg-[#E8F1FF] border border-[#BFDBFE] rounded-full pl-3 pr-2 py-[5px] text-[12px] font-semibold text-[#1B6FE0] ${className}`}
  >
    {children}
    <button
      type="button"
      onClick={onRemove}
      aria-label={removeLabel}
      className="w-4 h-4 rounded-full bg-white text-[#2283FF] text-[10px] leading-none inline-flex items-center justify-center flex-none"
    >
      ×
    </button>
  </span>
);
